import {
  Button,
  Card,
  Checkbox,
  Flex,
  HStack,
  Icon,
  Skeleton,
  Table,
  Tag,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import * as React from 'react'

import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { DeleteClusterDangerZone } from '@/components/DeleteClusterDangerZone/DeleteClusterDangerZone'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { useCluster } from '@/contexts/useCluster'
import { configRevisionComparison } from '@/features/ClusterConfigs/components/States/ConfigRevisionComparison'
import { RevisionState } from '@/features/ClusterConfigs/components/States/RevisionState'
import { clusterConfigDiffRoute } from '@/features/ClusterConfigs/pages/ClusterConfigDiff/ClusterRevisionDetails'
import { clusterConfigsRootRoute } from '@/features/ClusterConfigs/pages/ClusterConfigsPage/ClusterConfigsPage'
import {
  useGetCluster,
  useGetClusterRevisions,
} from '@/generated/http-clients/v3ApiComponents'
import type {
  ClusterHead,
  LatestClusterRevision,
  Revision,
} from '@/generated/http-clients/v3ApiSchemas'
import { timeAgo } from '@/helpers/timeFormatter'
import { NBSP } from '@/utils'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link, createRoute, useNavigate } from '@tanstack/react-router'
import capitalize from 'lodash/capitalize'
import { LoaderCircle, Plus, RefreshCw } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ClusterConfigCombinedStates } from '../../components/States/ClusterConfigCombinedStates'
import { clusterConfigDraftRoute } from '../ClusterConfigDraft/ClusterConfigDraft'
import { clusterConfigTableRoute } from '../ClusterConfigTable/ClusterConfigTable'
import styles from './clusterConfigsRevisions.module.css'

export const clusterConfigRevisionsRoute = createRoute({
  getParentRoute: () => clusterConfigsRootRoute,
  path: '/',
  component: () => <ClusterConfigRevisions />,
})

function ClusterConfigRevisions() {
  const nav = useNavigate()
  const pathParams = clusterConfigRevisionsRoute.useParams()
  const cluster = useGetCluster({ pathParams })
  const { t } = useTranslation(undefined, { useSuspense: false })
  const clusterContext = useCluster()
  const { isResyncing } = useCluster()

  const clusterRevisions = useGetClusterRevisions({
    pathParams,
  })

  const activeRevision = clusterRevisions.data?.find((revision: Revision) => {
    return revision.approval_state.state === 'APPLIED'
  })

  const [selectedRows, setSelectedRows] = React.useState<string[]>([])
  const breadCrumbs = [
    { name: t('sidebar.v3ClusterRevisions'), path: clusterConfigTableRoute.to },
    {
      name: pathParams.clusterId,
      path: '',
    },
  ]

  function onChange(id: string) {
    if (selectedRows.includes(id)) {
      setSelectedRows((rows) => rows.filter((row) => row !== id))
    } else {
      setSelectedRows((rows) => [...rows, id])
    }
  }

  useEffect(() => {
    if (cluster.isStale) {
      cluster.refetch()
    }
  }, [cluster])

  function handleCompare() {
    nav({
      to: configRevisionComparison.to,
      params: {
        clusterId: pathParams.clusterId,
        orgId: pathParams.orgId,
        revisionId0: selectedRows[0],
        revisionId1: selectedRows[1],
      },
    })
  }

  // sort revisions by revision ID descending (latest revision on top)
  function sortRevisions(revisionA: Revision, revisionB: Revision) {
    return (
      Number.parseInt(revisionB.revision_id) -
      Number.parseInt(revisionA.revision_id)
    )
  }

  function combineIntoLatestClusterRevision(
    revision: Revision,
    { disabled, hive_state, rollout, system, template_id }: ClusterHead,
  ): LatestClusterRevision {
    return {
      revision: {
        hive_state,
        approval_state: revision.approval_state,
        rollout,
        template_id,
        hive_id: pathParams.clusterId,
        hive_name: system?.global?.name ?? '',
        org_id: pathParams.orgId,
        deployment_label: system?.global?.deployment_label ?? '',
      },
      disabled,
    }
  }

  function revisionActionMetadata(revision: Revision) {
    let time = revision.updated_at
      ? timeAgo(revision.updated_at ?? '')
      : undefined
    const isSubmitted = revision.approval_state.state === 'SUBMITTED'
    let author = isSubmitted ? revision.created_by : revision.approved_by
    if (!author) {
      author = revision.created_by
    }
    const by = author ? `by ${author}` : ''
    // only in the submitted case: if updated_at is undefined but created_at is, use that field
    if (isSubmitted && !time && revision.created_at) {
      time = timeAgo(revision.created_at)
    }

    const prefix = time ? `${time} ` : ''
    return `${prefix}${by}`
  }

  const sortedRevisions = clusterRevisions.data
    ? clusterRevisions.data.toSorted(sortRevisions)
    : []

  function triggerResync() {
    clusterContext.triggerClusterResync(pathParams.orgId, pathParams.clusterId)
  }

  return (
    <PageLayout
      title={cluster.data?.system?.global?.name}
      description={
        <Text m={0} color="text.text-subtle">
          You can create a new draft revision for this hive from scratch, or
          open an existing revision and create a new one based on it.
        </Text>
      }
      beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      action={
        <Flex gap="4">
          {(clusterRevisions.data?.length ?? 0) > 1 ? (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <Button
                  variant="outline"
                  onClick={handleCompare}
                  disabled={selectedRows.length !== 2}
                >
                  Compare changes
                </Button>
              </Tooltip.Trigger>
              {selectedRows.length !== 2 && (
                <Tooltip.Positioner>
                  <Tooltip.Content>
                    Select 2 revisions to compare
                  </Tooltip.Content>
                </Tooltip.Positioner>
              )}
            </Tooltip.Root>
          ) : null}
          <Link
            to={clusterConfigDraftRoute.to}
            params={{
              ...pathParams,
              baseRevisionId: activeRevision?.revision_id ?? '',
            }}
          >
            <Button colorPalette="brand">
              <Icon as={Plus} />
              New draft revision
            </Button>
          </Link>
        </Flex>
      }
    >
      {clusterRevisions.isError || cluster.data ? (
        <ApiErrorState
          apiResource={cluster as unknown as UseQueryResult}
          errorTitle="An error happened"
          errorDescription="We failed to load the data from the server for the revisions."
        />
      ) : null}
      {clusterRevisions.isLoading ? <Skeleton h="500px" w="100%" /> : null}
      {clusterRevisions.isSuccess ? (
        <Card.Root w="100%">
          <Table.Root variant="outline">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader width="32px" paddingRight="0" />
                <Table.ColumnHeader width="32px" paddingRight="0" />
                <Table.ColumnHeader>Revision ID</Table.ColumnHeader>
                <Table.ColumnHeader />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sortedRevisions.map((revision: Revision) => {
                return (
                  <Table.Row
                    key={revision.revision_id}
                    className={styles.trAlignContentTop}
                    _hover={{
                      backgroundColor: 'var(--chakra-colors-shell-muted)',
                    }}
                  >
                    <Table.Cell className={styles.trAlignContentTopTdCheckbox}>
                      <Checkbox.Root
                        onCheckedChange={() => onChange(revision.revision_id)}
                        disabled={
                          selectedRows.length === 2 &&
                          !selectedRows.includes(revision.revision_id)
                        }
                        checked={selectedRows.includes(revision.revision_id)}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                      </Checkbox.Root>
                    </Table.Cell>
                    <Table.Cell className={styles.trAlignContentTopTdIcon}>
                      {/* if it's the active revision, we can display a better (combined) state for that one */}
                      {revision.revision_id ===
                      cluster.data?.rollout?.revision_id ? (
                        <ClusterConfigCombinedStates
                          revision={combineIntoLatestClusterRevision(
                            revision,
                            cluster.data,
                          )}
                        />
                      ) : (
                        <RevisionState
                          approvalState={revision.approval_state}
                        />
                      )}
                    </Table.Cell>

                    <Table.Cell
                      className={styles.trAlignContentTopTdRevision}
                      onClick={() => {
                        nav({
                          to: clusterConfigDiffRoute.to,
                          params: {
                            ...pathParams,
                            revisionId: revision.revision_id,
                          },
                        })
                      }}
                    >
                      <VStack gap={1} alignItems="start">
                        <HStack>
                          <Link
                            to={clusterConfigDiffRoute.to}
                            params={{
                              ...pathParams,
                              revisionId: revision.revision_id,
                            }}
                          >
                            <Text
                              fontWeight={700}
                              _hover={{
                                textDecoration: 'underline',
                              }}
                            >
                              {revision.revision_id}
                            </Text>
                          </Link>
                          {revision.revision_id ===
                            cluster.data?.rollout?.revision_id && (
                            <Tag.Root colorPalette="green">
                              <Tag.Label>active</Tag.Label>
                            </Tag.Root>
                          )}
                        </HStack>
                        <Text fontSize="sm" color="gray">
                          {capitalize(revision.approval_state.state)}
                          {NBSP}
                          {revisionActionMetadata(revision)}
                        </Text>
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      {revision.revision_id ===
                        cluster.data?.rollout?.revision_id && (
                        <Tooltip.Root positioning={{ placement: 'top' }}>
                          <Tooltip.Trigger asChild>
                            <Button
                              onClick={triggerResync}
                              disabled={cluster.data?.ignore_for_reconcile}
                              loading={isResyncing}
                              loadingText="Syncing..."
                              w="172px"
                            >
                              <Icon
                                as={isResyncing ? LoaderCircle : RefreshCw}
                                className={isResyncing ? styles.rotatingCw : ''}
                              />
                              Re-sync cluster
                            </Button>
                          </Tooltip.Trigger>
                          {!isResyncing && (
                            <Tooltip.Positioner>
                              <Tooltip.Content>
                                <Tooltip.Arrow />
                                {cluster.data?.ignore_for_reconcile
                                  ? "Sync is currently disabled. To enable syncing, the 'ignore-for-reconcile' annotation needs to be removed."
                                  : 'Triggers a re-sync of the cluster configuration. This may take 30-60 seconds to complete.'}
                              </Tooltip.Content>
                            </Tooltip.Positioner>
                          )}
                        </Tooltip.Root>
                      )}
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
        </Card.Root>
      ) : null}

      <DeleteClusterDangerZone
        orgId={pathParams.orgId}
        clusterId={pathParams.clusterId}
        isDeleting={cluster.data?.hive_state?.state === 'DELETING'}
        onDelete={clusterContext.deleteOrgCluster}
      />
    </PageLayout>
  )
}
